import React, { useState, useMemo, useEffect } from 'react';
import { Plus, MapPin, Edit, Search, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import useAppStore from '../store';
import MapComponent from '../components/Map';
import { Client } from '../types';
import { supabase } from '../lib/supabase';

// Coordonnées du dépôt de Bourg de Visa
const BOURG_DE_VISA_POSITION = { lat: 44.260571, lng: 0.960050 };

interface ClientLocation {
  id: string;
  name: string;
  position_lat: number;
  position_lng: number;
  is_primary: boolean;
  client_id: string;
}

const ClientsPage: React.FC = () => {
  const navigate = useNavigate();
  const { clients, addClient, updateClient, currentUser, dumpsters, loadInitialData } = useAppStore();
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [isPickingLocation, setIsPickingLocation] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [selectedClientLocationForEdit, setSelectedClientLocationForEdit] = useState<ClientLocation | null>(null);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [clientLocations, setClientLocations] = useState<Record<string, ClientLocation[]>>({});
  const [newLocation, setNewLocation] = useState({
    name: '',
    lat: '',
    lng: '',
    isPrimary: false
  });

  // État pour le nouveau client
  const [newClient, setNewClient] = useState({
    firstName: '',
    lastName: '',
    companyName: '',
    streetAddress: '',
    postalCode: '',
    city: '',
    email: '',
    phone: ''
  });

  // État pour l'édition du client
  const [editClient, setEditClient] = useState({
    firstName: '',
    lastName: '',
    companyName: '',
    streetAddress: '',
    postalCode: '',
    city: '',
    email: '',
    phone: ''
  });

  // État pour le formulaire d'édition de point GPS
  const [showEditLocationForm, setShowEditLocationForm] = useState(false);
  const [editLocationFormData, setEditLocationFormData] = useState({
    name: '',
    position_lat: '',
    position_lng: ''
  });
  const [editLocationError, setEditLocationError] = useState<string | null>(null);
  const [editLocationSuccess, setEditLocationSuccess] = useState(false);

  // Initialiser le formulaire d'édition quand un client est sélectionné
  useEffect(() => {
    if (selectedClient) {
      setEditClient({
        firstName: selectedClient.firstName,
        lastName: selectedClient.lastName,
        companyName: selectedClient.companyName,
        streetAddress: selectedClient.streetAddress,
        postalCode: selectedClient.postalCode,
        city: selectedClient.city,
        email: selectedClient.email || '',
        phone: selectedClient.phone || ''
      });
    }
  }, [selectedClient]);

  const loadClientLocations = async (clientId: string) => {
    try {
      const { data, error } = await supabase
        .from('client_locations')
        .select('*')
        .eq('client_id', clientId)
        .order('is_primary', { ascending: false });

      if (error) throw error;

      setClientLocations(prev => ({
        ...prev,
        [clientId]: data || []
      }));
    } catch (error) {
      console.error('Error loading client locations:', error);
      alert('Une erreur est survenue lors du chargement des points GPS');
    }
  };

  useEffect(() => {
    if (selectedClient) {
      loadClientLocations(selectedClient.id);
    }
  }, [selectedClient]);

  const filteredClients = useMemo(() => {
    return clients
      .filter(client => {
        const searchLower = searchTerm.toLowerCase();
        return (
          client.firstName.toLowerCase().includes(searchLower) ||
          client.lastName.toLowerCase().includes(searchLower) ||
          client.companyName.toLowerCase().includes(searchLower)
        );
      })
      .sort((a, b) => a.companyName.localeCompare(b.companyName));
  }, [clients, searchTerm]);

  const handleMapClick = (e: { latlng: { lat: number; lng: number } }) => {
    if (isPickingLocation) {
      setNewLocation(prev => ({
        ...prev,
        lat: e.latlng.lat.toFixed(6),
        lng: e.latlng.lng.toFixed(6)
      }));
      setSelectedLocation(e.latlng);
      setIsPickingLocation(false);
      setShowLocationModal(true);
    }
  };

  const handleLocationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedClient) return;

    try {
      const { error } = await supabase
        .from('client_locations')
        .insert({
          client_id: selectedClient.id,
          name: newLocation.name,
          position_lat: parseFloat(newLocation.lat),
          position_lng: parseFloat(newLocation.lng),
          is_primary: newLocation.isPrimary,
          created_by: currentUser?.id
        });

      if (error) throw error;

      setShowLocationModal(false);
      setSelectedLocation(null);
      setNewLocation({
        name: '',
        lat: '',
        lng: '',
        isPrimary: false
      });

      loadClientLocations(selectedClient.id);
    } catch (error) {
      console.error('Error adding location:', error);
      alert('Une erreur est survenue lors de l\'ajout du point GPS');
    }
  };

  const handleEditLocationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!selectedClientLocationForEdit) {
      setEditLocationError('Location non sélectionnée');
      return;
    }

    if (!editLocationFormData.name) {
      setEditLocationError('Le nom est requis');
      return;
    }

    const lat = parseFloat(editLocationFormData.position_lat);
    const lng = parseFloat(editLocationFormData.position_lng);

    if (isNaN(lat) || isNaN(lng)) {
      setEditLocationError('Coordonnées GPS invalides');
      return;
    }

    try {
      setEditLocationError(null);

      const { error } = await supabase
        .from('client_locations')
        .update({
          name: editLocationFormData.name,
          position_lat: lat,
          position_lng: lng
        })
        .eq('id', selectedClientLocationForEdit.id);

      if (error) throw error;

      // Fermer le modal d'édition
      setShowEditLocationForm(false);

      // Réinitialiser les données du formulaire
      setEditLocationFormData({
        name: '',
        position_lat: '',
        position_lng: ''
      });

      // Désélectionner le client et le point GPS
      setSelectedClientLocationForEdit(null);
      setSelectedClient(null);
      setClientLocations({});
      
      // Afficher un message de succès temporaire
      setEditLocationSuccess(true);
      
      setTimeout(() => {
        setEditLocationSuccess(false);
        // Rediriger vers la page d'accueil
        navigate('/');
      }, 1000);

      await loadInitialData();

    } catch (error) {
      console.error('Error updating location:', error);
      setEditLocationError('Une erreur est survenue lors de la modification du point GPS');
    }
  };

  const handleAddLocationClick = () => {
    if (!selectedClient) {
      alert('Veuillez sélectionner un client d\'abord');
      return;
    }
    setIsPickingLocation(true);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setSelectedClient(null);
    setSelectedLocation(null);
    setClientLocations({});
  };

  const handleAddClientSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await addClient({
        ...newClient,
        position: BOURG_DE_VISA_POSITION
      });
      setShowAddModal(false);
      setNewClient({
        firstName: '',
        lastName: '',
        companyName: '',
        streetAddress: '',
        postalCode: '',
        city: '',
        email: '',
        phone: ''
      });
    } catch (error) {
      console.error('Error adding client:', error);
      alert('Une erreur est survenue lors de la création du client');
    }
  };

  const handleEditClientSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedClient) return;

    try {
      await updateClient(selectedClient.id, editClient);
      setShowEditModal(false);
      setSelectedClient(null);
    } catch (error) {
      console.error('Error updating client:', error);
      alert('Une erreur est survenue lors de la modification du client');
    }
  };

  // Filter dumpsters that are not in a depot
  const activeDumpsters = useMemo(() => {
    return dumpsters
      .filter(d => !d.depotId)
      .map(d => ({
        ...d,
        selected: false
      }));
  }, [dumpsters]);

  const isAdmin = currentUser?.role === 'admin';
  const isLogistics = currentUser?.role === 'logistics';
  const canEditLocation = isAdmin || isLogistics;

  return (
    <div className="h-[calc(100vh-8rem)]">
      <div className="bg-white rounded-lg shadow mb-4 p-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 relative z-30">
        <div className="flex-1 max-w-md relative w-full">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
          <input
            type="text"
            placeholder="Rechercher un client..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-10 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {(searchTerm || selectedClient) && (
            <button
              onClick={handleClearSearch}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          )}
          {searchTerm && filteredClients.length > 0 && (
            <div className="absolute w-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 max-h-64 overflow-y-auto z-40">
              {filteredClients.map(client => (
                <button
                  key={client.id}
                  onClick={() => {
                    setSelectedClient(client);
                    setSearchTerm('');
                  }}
                  className="w-full text-left px-4 py-2 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none"
                >
                  <div className="font-medium">{client.companyName}</div>
                  <div className="text-sm text-gray-600">
                    {client.firstName} {client.lastName}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center space-x-2 w-full sm:w-auto">
          <button
            onClick={() => setShowAddModal(true)}
            className="bg-blue-800 text-white px-4 py-2 rounded-lg flex items-center w-full sm:w-auto justify-center sm:justify-start"
          >
            <Plus className="h-4 w-4 mr-2" />
            Ajouter un client
          </button>
          {selectedClient && (
            <>
              <button
                onClick={handleAddLocationClick}
                className="text-blue-600 hover:text-blue-900 p-2"
                title="Ajouter un point GPS"
              >
                <MapPin className="h-5 w-5" />
              </button>
              <button
                onClick={() => {
                  setShowEditModal(true);
                }}
                className="text-blue-600 hover:text-blue-900 p-2"
                title="Modifier"
              >
                <Edit className="h-5 w-5" />
              </button>
            </>
          )}
        </div>
      </div>

      {selectedClient && (
        <div className="bg-white rounded-lg shadow p-4 mb-4 relative z-20">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
            <div>
              <h3 className="text-xl font-bold">{selectedClient.companyName}</h3>
              <p className="text-gray-600">
                {selectedClient.firstName} {selectedClient.lastName}
              </p>
            </div>
            {clientLocations[selectedClient.id]?.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 mt-2 sm:mt-0">
                <div className="text-sm text-gray-600 w-full sm:w-auto">
                  Points GPS : {clientLocations[selectedClient.id].length}
                </div>
                <div className="flex flex-wrap gap-2">
                  {clientLocations[selectedClient.id].map(location => (
                    <button
                      key={location.id}
                      onClick={() => setSelectedLocation({
                        lat: location.position_lat,
                        lng: location.position_lng
                      })}
                      className={`px-3 py-1 rounded-lg text-sm ${
                        location.is_primary
                          ? 'bg-blue-100 text-blue-800 font-medium'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {location.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="h-full bg-white rounded-lg shadow relative z-10">
        <MapComponent 
          showDrivers={false} 
          showDepots={false}
          onClick={handleMapClick}
          center={selectedLocation || (selectedClient ? {
            lat: selectedClient.position.lat,
            lng: selectedClient.position.lng
          } : BOURG_DE_VISA_POSITION)}
          clientLocations={clientLocations[selectedClient?.id || ''] || []}
          isPickingLocation={isPickingLocation}
          dumpsters={activeDumpsters}
          showSaveGpsButton={false}
          navigate={navigate}
        />
        {isPickingLocation && (
          <div className="absolute top-0 left-0 right-0 p-4 flex justify-center z-20">
            <div className="bg-white p-4 rounded-lg shadow-lg text-center">
              <p className="text-lg font-medium mb-2">Cliquez sur la carte pour sélectionner la position</p>
              <button
                onClick={() => setIsPickingLocation(false)}
                className="text-blue-800 hover:text-blue-600"
              >
                Annuler
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal d'ajout de client */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Ajouter un client</h2>
            
            <form onSubmit={handleAddClientSubmit}>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Prénom
                  </label>
                  <input
                    type="text"
                    value={newClient.firstName}
                    onChange={(e) => setNewClient(prev => ({ ...prev, firstName: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Nom
                  </label>
                  <input
                    type="text"
                    value={newClient.lastName}
                    onChange={(e) => setNewClient(prev => ({ ...prev, lastName: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 font-medium mb-2">
                  Nom de l'entreprise
                </label>
                <input
                  type="text"
                  value={newClient.companyName}
                  onChange={(e) => setNewClient(prev => ({ ...prev, companyName: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 font-medium mb-2">
                  Adresse
                </label>
                <input
                  type="text"
                  value={newClient.streetAddress}
                  onChange={(e) => setNewClient(prev => ({ ...prev, streetAddress: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Code postal
                  </label>
                  <input
                    type="text"
                    value={newClient.postalCode}
                    onChange={(e) => setNewClient(prev => ({ ...prev, postalCode: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                    pattern="\d{5}"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Ville
                  </label>
                  <input
                    type="text"
                    value={newClient.city}
                    onChange={(e) => setNewClient(prev => ({ ...prev, city: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={newClient.email}
                    onChange={(e) => setNewClient(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Téléphone
                  </label>
                  <div className="text-sm text-gray-500 mb-1">
                    (format +33XXXXXXXXX)
                  </div>
                  <input
                    type="tel"
                    value={newClient.phone}
                    onChange={(e) => setNewClient(prev => ({ ...prev, phone: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="+33612345678"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Annuler
                </button>
                
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-800 text-white rounded-lg hover:bg-blue-700"
                >
                  Ajouter
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal d'édition de client */}
      {showEditModal && selectedClient && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Modifier le client</h2>
            
            <form onSubmit={handleEditClientSubmit}>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Prénom
                  </label>
                  <input
                    type="text"
                    value={editClient.firstName}
                    onChange={(e) => setEditClient(prev => ({ ...prev, firstName: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Nom
                  </label>
                  <input
                    type="text"
                    value={editClient.lastName}
                    onChange={(e) => setEditClient(prev => ({ ...prev, lastName: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 font-medium mb-2">
                  Nom de l'entreprise
                </label>
                <input
                  type="text"
                  value={editClient.companyName}
                  onChange={(e) => setEditClient(prev => ({ ...prev, companyName: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 font-medium mb-2">
                  Adresse
                </label>
                <input
                  type="text"
                  value={editClient.streetAddress}
                  onChange={(e) => setEditClient(prev => ({ ...prev, streetAddress: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Code postal
                  </label>
                  <input
                    type="text"
                    value={editClient.postalCode}
                    onChange={(e) => setEditClient(prev => ({ ...prev, postalCode: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                    pattern="\d{5}"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Ville
                  </label>
                  <input
                    type="text"
                    value={editClient.city}
                    onChange={(e) => setEditClient(prev => ({ ...prev, city: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={editClient.email}
                    onChange={(e) => setEditClient(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Téléphone
                  </label>
                  <div className="text-sm text-gray-500 mb-1">
                    (format +33XXXXXXXXX)
                  </div>
                  <input
                    type="tel"
                    value={editClient.phone}
                    onChange={(e) => setEditClient(prev => ({ ...prev, phone: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="+33612345678"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Annuler
                </button>
                
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-800 text-white rounded-lg hover:bg-blue-700"
                >
                  Enregistrer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal d'ajout de point GPS */}
      {showLocationModal && selectedLocation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Ajouter un point GPS</h2>
            
            <form onSubmit={handleLocationSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Nom du point
                  </label>
                  <input
                    type="text"
                    value={newLocation.name}
                    onChange={(e) => setNewLocation(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Position
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="text"
                      value={newLocation.lat}
                      onChange={canEditLocation ? (e) => setNewLocation(prev => ({ ...prev, lat: e.target.value })) : undefined}
                      readOnly={!canEditLocation}
                      className={`w-full px-3 py-2 border rounded-lg ${!canEditLocation ? 'bg-gray-50' : 'focus:outline-none focus:ring-2 focus:ring-blue-500'}`}
                    />
                    <input
                      type="text"
                      value={newLocation.lng}
                      onChange={canEditLocation ? (e) => setNewLocation(prev => ({ ...prev, lng: e.target.value })) : undefined}
                      readOnly={!canEditLocation}
                      className={`w-full px-3 py-2 border rounded-lg ${!canEditLocation ? 'bg-gray-50' : 'focus:outline-none focus:ring-2 focus:ring-blue-500'}`}
                    />
                  </div>
                </div>

                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={newLocation.isPrimary}
                      onChange={(e) => setNewLocation(prev => ({ ...prev, isPrimary: e.target.checked }))}
                      className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                    />
                    <span className="ml-2 text-sm text-gray-600">Point principal</span>
                  </label>
                </div>
              </div>

              <div className="flex justify-end space-x-2 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowLocationModal(false);
                    setSelectedLocation(null);
                    setNewLocation({
                      name: '',
                      lat: '',
                      lng: '',
                      isPrimary: false
                    });
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Annuler
                </button>
                
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-800 text-white rounded-lg hover:bg-blue-700"
                >
                  Ajouter
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal d'édition de point GPS */}
      {showEditLocationForm && selectedClientLocationForEdit && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Modifier le point GPS</h2>
            
            <form onSubmit={handleEditLocationSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Nom du point
                  </label>
                  <input
                    type="text"
                    value={editLocationFormData.name}
                    onChange={(e) => setEditLocationFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Position
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="text"
                      value={editLocationFormData.position_lat}
                      onChange={(e) => setEditLocationFormData(prev => ({ ...prev, position_lat: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                    <input
                      type="text"
                      value={editLocationFormData.position_lng}
                      onChange={(e) => setEditLocationFormData(prev => ({ ...prev, position_lng: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                </div>

                {editLocationError && (
                  <div className="text-sm text-red-600">{editLocationError}</div>
                )}

                {editLocationSuccess && (
                  <div className="text-sm text-green-600">Point GPS modifié avec succès</div>
                )}

                <div className="flex space-x-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowEditLocationForm(false);
                      setEditLocationError(null);
                      setSelectedLocation(null);
                      setEditLocationFormData({
                        name: '',
                        position_lat: '',
                        position_lng: ''
                      });
                    }}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Annuler
                  </button>
                  
                  <button
                    type="submit"
                    className="flex-1 px-3 py-2 bg-blue-800 text-white rounded-lg hover:bg-blue-700"
                  >
                    Enregistrer
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientsPage;